import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Plus, Ticket as TicketIcon } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import SteamLoginButton from "@/components/SteamLoginButton";

function fmt(iso) { try { return new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }); } catch { return ""; } }

const STATUS_STYLES = {
  open: { label: "Ouvert", variant: "default" },
  in_progress: { label: "En cours", variant: "secondary" },
  closed: { label: "Fermé", variant: "outline" },
};

export default function TicketsPage() {
  const { user, loading } = useAuth();
  const { data: tickets = [], isLoading } = useQuery({
    queryKey: ["my-tickets"],
    queryFn: () => api.get("/tickets/mine").then((r) => r.data),
    enabled: !!user,
  });

  if (loading) return <div className="py-24 text-center text-muted-foreground">Chargement...</div>;
  if (!user) {
    return (
      <div className="max-w-lg mx-auto py-16 px-4 text-center">
        <TicketIcon className="h-12 w-12 mx-auto text-primary mb-3" />
        <p className="font-display uppercase text-2xl mb-2">Connexion requise</p>
        <p className="text-muted-foreground text-sm mb-4">Connectez-vous avec Steam pour ouvrir un ticket.</p>
        <SteamLoginButton />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-16">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
        <div>
          <h1 className="font-display text-4xl font-black uppercase tracking-tight">Mes tickets</h1>
          <p className="text-muted-foreground mt-2">Vos demandes envoyées au staff. Seul vous et le staff pouvez les consulter.</p>
        </div>
        <Button asChild data-testid="new-ticket-button"><Link to="/tickets/nouveau"><Plus className="h-4 w-4 mr-1" /> Nouveau ticket</Link></Button>
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">Chargement...</p>
      ) : tickets.length === 0 ? (
        <Card><CardContent className="p-10 text-center text-muted-foreground">
          Aucun ticket pour le moment. Cliquez sur « Nouveau ticket » pour ouvrir votre première demande.
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {tickets.map((t) => {
            const s = STATUS_STYLES[t.status] || STATUS_STYLES.open;
            return (
              <Link key={t.id} to={`/tickets/${t.id}`} data-testid={`ticket-item-${t.id}`}>
                <Card className="hover:border-primary/60 transition-colors">
                  <CardContent className="p-4 flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <Badge variant="outline" className="font-mono-tech uppercase text-[10px]">{t.type_label}</Badge>
                        <Badge variant={s.variant}>{s.label}</Badge>
                      </div>
                      <p className="font-medium truncate">{t.subject}</p>
                      <p className="text-xs text-muted-foreground mt-1">{fmt(t.updated_at)} · {t.messages?.length || 0} message(s)</p>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
